import { memo, useMemo } from 'react';

import { UseQueryResult } from 'react-query';
import { AxiosError } from 'axios';

import { FullScheduleResult } from '@common/types/api';
import { useEventfy } from '@hooks';
import { selectIsGamificationRankingEnabled } from '@hooks/useEventfy/selectors';
import { selectIsNewsFeedEnable } from '@hooks/useEventfy/selectors';

import SchedulePanel from './SchedulePanel';
import FeedPanel from './FeedPanel';
import RankingPanel from './RankingPanel';
import { useCarousel } from './useCarousel';
import * as S from './styles';

type LobbySidebarProps = {
  scheduleQuery: UseQueryResult<FullScheduleResult, AxiosError>;
};

const ROTATE_INTERVAL = 8000;

const LobbySidebar = ({ scheduleQuery }: LobbySidebarProps) => {
  const isFeedEnabled = useEventfy(selectIsNewsFeedEnable);
  const isRankingEnabled = useEventfy(selectIsGamificationRankingEnabled);

  const panels = useMemo(() => {
    const list: { key: string; component: JSX.Element }[] = [];
    if (isFeedEnabled) {
      list.push({ key: 'feed', component: <FeedPanel /> });
    }
    if (isRankingEnabled) {
      list.push({ key: 'ranking', component: <RankingPanel /> });
    }
    return list;
  }, [isFeedEnabled, isRankingEnabled]);

  const carousel = useCarousel(panels.length, ROTATE_INTERVAL);

  if (!panels.length) {
    return (
      <S.Container>
        <SchedulePanel scheduleQuery={scheduleQuery} />
      </S.Container>
    );
  }

  return (
    <S.Container>
      <SchedulePanel scheduleQuery={scheduleQuery} />

      <S.BottomPanel>
        {panels.length > 1 && (
          <S.CarouselNav>
            {panels.map((panel, i) => (
              <S.CarouselDot
                key={panel.key}
                $active={carousel.activeIndex === i}
                onClick={() => carousel.goTo(i)}
              />
            ))}
          </S.CarouselNav>
        )}

        <S.CarouselTrack>
          {panels.map((panel, i) => (
            <S.CarouselSlide
              key={panel.key}
              $visible={carousel.activeIndex === i}
            >
              {panel.component}
            </S.CarouselSlide>
          ))}
        </S.CarouselTrack>
      </S.BottomPanel>
    </S.Container>
  );
};

export default memo(LobbySidebar);
